import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { supabase, supabaseHelpers } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./Sending.css";

// Flag imports
import aeflag from "../../assets/images/flags/ae_flag.png";
import frflag from "../../assets/images/flags/fr_flag.png";
import lbflag from "../../assets/images/flags/lb_flag.svg";
import qaflag from "../../assets/images/flags/qa_flag.svg";
import saflag from "../../assets/images/flags/saudi_flag.webp";
import syflag from "../../assets/images/flags/syria_flag.webp";
import usflag from "../../assets/images/flags/us_flag.png";

const FLAG_MAP = {
  AED: aeflag,
  EUR: frflag,
  LBP: lbflag,
  QAR: qaflag,
  SAR: saflag,
  SYP: syflag,
  USD: usflag,
};

const DEFAULT_AVATAR =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjQiIGN5PSIyNCIgcj0iMjQiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMiIgeT0iMTIiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

function resolveAvatarUrl(userId, avatarFilename) {
  if (!avatarFilename) return DEFAULT_AVATAR;
  if (typeof avatarFilename === "string" && avatarFilename.startsWith("data:")) return avatarFilename;
  if (typeof avatarFilename === "string" && /^https?:\/\//i.test(avatarFilename)) return avatarFilename;
  try {
    const { data } = supabase.storage.from("avatars").getPublicUrl(`${userId}/${avatarFilename}`);
    return data?.publicUrl || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
}

const asNumber = (v) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

async function fetchWinicoinsBalance(userId) {
  // Prefer wallet_view (if it exists) because it may be decoded already
  try {
    const { data, error } = await supabase
      .from("wallet_view")
      .select("winicoins")
      .eq("user_id", userId)
      .single();
    if (!error && data && data.winicoins != null) return asNumber(data.winicoins);
  } catch {
    // ignore
  }

  // Fallback: wallet table (might be bytea; numeric decode may be handled by view/rls)
  const { data, error } = await supabase
    .from("wallet")
    .select("winicoins")
    .eq("user_id", userId)
    .single();
  if (error || !data) return 0;
  // Best-effort decode
  const raw = data.winicoins;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (raw instanceof Uint8Array) {
    try {
      const decoded = new TextDecoder().decode(raw);
      const parsed = parseFloat(decoded);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch {
      return 0;
    }
  }
  return 0;
}

export default function Sending() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [params] = useSearchParams();
  const recipientId = params.get("to") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [recipient, setRecipient] = useState(null);
  const [balance, setBalance] = useState(0);
  const [feePct, setFeePct] = useState(10);
  const [winiToUsdRate, setWiniToUsdRate] = useState(12);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [tab, setTab] = useState("wini"); // "wini" | "currency"
  const [amount, setAmount] = useState("");
  const [feePayer, setFeePayer] = useState("sender"); // "sender" | "recipient"
  const [note, setNote] = useState("");
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      if (!currentUser?.id || !recipientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [{ data: p, error: pErr }, { data: s, error: sErr }, { data: currData, error: currErr }] = await Promise.all([
          supabase
            .from("profiles")
            .select("user_id, username, display_name, avatar_url")
            .eq("user_id", recipientId)
            .single(),
          supabase.from("app_settings").select("coins_transfer_percentage, wini_to_usd_rate").limit(1).single(),
          supabase.from("currencies").select("*").order("iso"),
        ]);

        if (pErr || !p) throw pErr || new Error("Recipient not found");
        const mapped = {
          user_id: p.user_id,
          username: p.username || "user",
          display_name: p.display_name || p.username || "User",
          avatar: resolveAvatarUrl(p.user_id, p.avatar_url),
        };

        const pct = sErr ? 10 : asNumber(s?.coins_transfer_percentage ?? 10);
        const rate = sErr ? 12 : asNumber(s?.wini_to_usd_rate ?? 12);

        // Load currencies for dropdown
        const currList = (currData || [])
          .filter((c) => c.rate && c.name)
          .map((c) => ({
            code: c.iso,
            name: c.name,
            rate: c.rate,
            flag: FLAG_MAP[c.iso] || FLAG_MAP.USD,
          }));

        const myBal = await fetchWinicoinsBalance(currentUser.id);

        if (cancelled) return;
        setRecipient(mapped);
        setFeePct(pct > 0 ? pct : 0);
        setWiniToUsdRate(rate > 0 ? rate : 12);
        setBalance(myBal);
        setCurrencies(currList);
        setSelectedCurrency(currList.find((c) => c.code === "USD") || currList[0] || null);
      } catch (e) {
        console.error("[Sending] boot error:", e);
        if (!cancelled) setError("Failed to load transfer screen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, recipientId]);

  const amountNum = useMemo(() => asNumber(amount), [amount]);

  // Convert currency amount to WIN if in currency tab
  const amountInWin = useMemo(() => {
    if (tab === "wini") return amountNum;
    if (!selectedCurrency || !winiToUsdRate) return amountNum;
    // Currency -> USD -> WIN
    const usd = amountNum / selectedCurrency.rate;
    return usd / winiToUsdRate;
  }, [amountNum, tab, selectedCurrency, winiToUsdRate]);

  const feeAmount = useMemo(() => (amountInWin * feePct) / 100, [amountInWin, feePct]);

  const senderDelta = useMemo(() => {
    if (!amountInWin) return 0;
    return feePayer === "sender" ? amountInWin + feeAmount : amountInWin;
  }, [amountInWin, feeAmount, feePayer]);

  const recipientDelta = useMemo(() => {
    if (!amountInWin) return 0;
    return feePayer === "recipient" ? Math.max(0, amountInWin - feeAmount) : amountInWin;
  }, [amountInWin, feeAmount, feePayer]);

  const insufficientBalance = useMemo(() => {
    return amountInWin > 0 && senderDelta > balance + 1e-9;
  }, [amountInWin, senderDelta, balance]);

  const canSubmit = useMemo(() => {
    if (!currentUser?.id || !recipientId) return false;
    if (!amountInWin || amountInWin <= 0) return false;
    if (senderDelta <= 0) return false;
    if (insufficientBalance) return false;
    if (!recipientDelta || recipientDelta <= 0) return false;
    if (recipientId === currentUser.id) return false;
    return true;
  }, [amountInWin, insufficientBalance, currentUser?.id, recipientDelta, recipientId, senderDelta]);

  const format = (n, digits = 2) =>
    Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

  const handleTransfer = useCallback(async () => {
    if (!canSubmit || submitting || !currentUser?.id || !recipientId) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      // Use the wallet transfer helper function
      const result = await supabaseHelpers.walletTransfer({
        fromUserId: currentUser.id,
        toUserId: recipientId,
        amount: amountInWin,
        feePayer: feePayer,
        description: note?.trim() || 'Transfer'
      });

      console.log("[Sending] transfer successful:", result);

      // Update local balance display
      const newBalance = await fetchWinicoinsBalance(currentUser.id);
      setBalance(newBalance);

      setSuccess("Transfer successful.");
      setAmount("");
      setNote("");
      // Navigate to transactions page after short delay
      setTimeout(() => navigate("/trans"), 700);
    } catch (e) {
      console.error("[Sending] transfer failed:", e);
      const errorMsg = e?.message || "Transfer failed. Please try again.";
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  }, [
    amountInWin,
    canSubmit,
    currentUser?.id,
    feePayer,
    navigate,
    note,
    recipientId,
    submitting,
  ]);

  if (loading) {
    return (
      <div className="sending-page">
        <div className="sending-shell">
          <div className="sending-loading">Loading…</div>
        </div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="sending-page">
        <div className="sending-shell">
          <div className="sending-error">{error || "Recipient not found."}</div>
          <button className="sending-backBtn" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sending-page">
      <div className="sending-shell">
        <div className="sending-topbar">
          <button className="send-back" onClick={() => navigate(-1)} aria-label="Back">
            <FiArrowLeft />
          </button>
          <div className="sending-titleWrap">
            <div className="sending-title">Transfer – {recipient.display_name}</div>
          </div>
          <button
            className="sending-topAvatar-btn"
            onClick={() => navigate(`/profile/${recipient.user_id}`)}
            aria-label={`View ${recipient.display_name}'s profile`}
          >
            <img className="sending-topAvatar" src={recipient.avatar} alt={recipient.display_name} />
          </button>
        </div>

        <div className="sending-tabs" role="tablist" aria-label="Transfer type">
          <button
            className={`sending-tab ${tab === "wini" ? "active" : ""}`}
            onClick={() => setTab("wini")}
            role="tab"
            aria-selected={tab === "wini"}
          >
            Wini&apos;s
          </button>
          <button
            className={`sending-tab ${tab === "currency" ? "active" : ""}`}
            onClick={() => setTab("currency")}
            role="tab"
            aria-selected={tab === "currency"}
          >
            Currency
          </button>
        </div>

        <div className="sending-section-transfer">
          <div className="sending-label-transfer">Amount</div>

          <div className="sending-amountRow-transfer">
            <input
              className="sending-amountInput-transfer"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="Enter amount"
            />
            {tab === "wini" ? (
              <div className="sending-unit-static">WIN</div>
            ) : (
              <button
                className="sending-currencyDropdownBtn"
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
              >
                {selectedCurrency && (
                  <>
                    <img
                      src={selectedCurrency.flag}
                      alt={selectedCurrency.code}
                      className="sending-currencyFlag"
                    />
                    <span className="sending-currencyCode">{selectedCurrency.code}</span>
                  </>
                )}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="#666"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Currency Dropdown */}
          {showCurrencyDropdown && tab === "currency" && (
            <div
              className="sending-currencyDropdownPortal"
              onClick={() => setShowCurrencyDropdown(false)}
            >
              <div
                className="sending-currencyDropdown"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sending-currencyDropdownHeader">
                  <input
                    autoFocus
                    className="sending-currencySearch"
                    placeholder="Search currency..."
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="sending-currencyOptions">
                  {currencies.map((curr) => (
                    <button
                      key={curr.code}
                      className={`sending-currencyOption ${selectedCurrency?.code === curr.code ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedCurrency(curr);
                        setShowCurrencyDropdown(false);
                      }}
                    >
                      <img src={curr.flag} alt={curr.code} className="sending-currencyOptionFlag" />
                      <div className="sending-currencyOptionText">
                        <div className="sending-currencyOptionLine">
                          <span className="sending-currencyOptionCode">{curr.code}</span>
                          <span className="sending-currencyOptionName">{curr.name}</span>
                        </div>
                        <div className="sending-currencyOptionRate">
                          1 USD ≈ {new Intl.NumberFormat("en-US").format(curr.rate)} {curr.code}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="sending-available-balance">
            Available: {format(balance, 10)} WIN
          </div>

          {/* Insufficient Balance Error */}
          {insufficientBalance && (
            <div className="sending-balanceError">
              ⚠️ Insufficient balance. The amount you're trying to send is more than what you have.
            </div>
          )}
        </div>

        <div className="sending-section">
          <div className="sending-label">Fee Deduction</div>

          <div className="sending-feePills">
            <button
              className={`sending-pill ${feePayer === "sender" ? "active" : ""}`}
              onClick={() => setFeePayer("sender")}
            >
              From Sender
            </button>
            <button
              className={`sending-pill ${feePayer === "recipient" ? "active" : ""}`}
              onClick={() => setFeePayer("recipient")}
            >
              From Recipient
            </button>
          </div>

          <div className="sending-summary">
            <div className="sending-summaryRow">
              <div className="muted">Transfer fee</div>
              <div>{format(feeAmount, 2)} ({format(feePct, 2)}%)</div>
            </div>
            <div className="sending-summaryRow">
              <div className="muted">You pay</div>
              <div>{format(senderDelta * winiToUsdRate, 2)} US Dollar</div>
            </div>
            <div className="sending-summaryRow">
              <div className="muted">Recipient receives</div>
              <div>{format(recipientDelta * winiToUsdRate, 2)} US Dollar</div>
            </div>
          </div>
        </div>

        <div className="sending-section">
          <div className="sending-label">Description (Optional)</div>
          <textarea
            className="sending-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this transfer..."
            rows={3}
          />
        </div>

        {error && <div className="sending-error">{error}</div>}
        {success && <div className="sending-success">{success}</div>}

        <button
          className={`sending-submit ${canSubmit ? "active" : ""}`}
          disabled={!canSubmit || submitting}
          onClick={handleTransfer}
        >
          {submitting ? "Transferring…" : "Transfer  WIN"}
        </button>
      </div>
    </div>
  );
}

